import { useState, useEffect, useRef } from 'react';
import { ticketsAPI } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { formatRelativeTime, priorityColors, statusColors } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Textarea } from '@/components/ui/textarea';
import {
  ArrowLeft,
  Send,
  Loader2,
  Clock,
  CheckCircle2
} from 'lucide-react';

export default function CustomerTicketView({ ticket, onBack }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentTicket, setCurrentTicket] = useState(ticket);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    loadMessages();
  }, [ticket.id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadMessages = async () => {
    try {
      setLoading(true);
      const [messagesRes, ticketRes] = await Promise.all([
        ticketsAPI.getMessages(ticket.id),
        ticketsAPI.get(ticket.id)
      ]);
      setMessages(messagesRes.data);
      setCurrentTicket(ticketRes.data);
    } catch (err) {
      console.error('Failed to load messages:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    try {
      setSending(true);
      await ticketsAPI.addMessage(ticket.id, {
        content: newMessage,
        is_internal: false
      });
      setNewMessage('');
      loadMessages();
    } catch (err) {
      console.error('Failed to send message:', err);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090b]" data-testid="customer-ticket-view">
      {/* Header */}
      <header className="border-b border-zinc-800 bg-zinc-950/50 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={onBack}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <span className="text-sm text-zinc-500 font-mono">{currentTicket.ticket_number}</span>
              <Badge className={statusColors[currentTicket.status]}>
                {currentTicket.status.replace('_', ' ')}
              </Badge>
              <Badge className={priorityColors[currentTicket.priority]}>
                {currentTicket.priority}
              </Badge>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Ticket Info */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold mb-2">{currentTicket.subject}</h1>
          <div className="flex items-center gap-4 text-sm text-zinc-500">
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              Created {formatRelativeTime(currentTicket.created_at)}
            </span>
            {currentTicket.assigned_agent_name && (
              <span>Assigned to: {currentTicket.assigned_agent_name}</span>
            )}
          </div>
        </div>

        {/* Status Timeline */}
        <div className="mb-8 p-4 bg-zinc-900/50 rounded-xl border border-zinc-800">
          <div className="flex items-center justify-between">
            {['open', 'in_progress', 'pending', 'resolved'].map((status, i) => {
              const isActive = currentTicket.status === status;
              const isPast = ['open', 'in_progress', 'pending', 'resolved'].indexOf(currentTicket.status) >= i;
              return (
                <div key={status} className="flex items-center">
                  <div className={`flex items-center gap-2 ${isPast ? 'text-indigo-400' : 'text-zinc-600'}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      isActive ? 'bg-indigo-500 text-white' : isPast ? 'bg-indigo-500/20' : 'bg-zinc-800'
                    }`}>
                      {isPast ? <CheckCircle2 className="w-4 h-4" /> : <span className="text-sm">{i + 1}</span>}
                    </div>
                    <span className="text-sm capitalize hidden sm:block">{status.replace('_', ' ')}</span>
                  </div>
                  {i < 3 && (
                    <div className={`w-12 h-0.5 mx-2 ${isPast ? 'bg-indigo-500' : 'bg-zinc-800'}`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Messages */}
        <div className="bg-zinc-900/50 rounded-xl border border-zinc-800 overflow-hidden">
          <ScrollArea className="h-[400px] p-4">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
              </div>
            ) : (
              <div className="space-y-4">
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`flex gap-3 ${msg.sender_id === user.id ? 'flex-row-reverse' : ''}`}
                    data-testid={`message-${msg.id}`}
                  >
                    <Avatar className="w-8 h-8 flex-shrink-0">
                      <AvatarFallback>{msg.sender_name?.charAt(0)}</AvatarFallback>
                    </Avatar>
                    <div className={`max-w-[70%] ${msg.sender_id === user.id ? 'text-right' : ''}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-sm">{msg.sender_name}</span>
                        <span className="text-xs text-zinc-500">
                          {formatRelativeTime(msg.created_at)}
                        </span>
                      </div>
                      <div className={`p-3 rounded-xl ${msg.sender_id === user.id 
                        ? 'bg-indigo-600 text-white' 
                        : 'bg-zinc-800 text-zinc-200'
                      }`}>
                        <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                      </div>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
            )}
          </ScrollArea>

          {/* Reply Box */}
          {currentTicket.status !== 'closed' && (
            <form onSubmit={handleSendMessage} className="p-4 border-t border-zinc-800">
              <div className="flex gap-2">
                <Textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type your reply..."
                  className="bg-zinc-800/50 border-zinc-700 min-h-[60px] resize-none"
                  data-testid="reply-input"
                />
                <Button 
                  type="submit" 
                  className="rounded-full bg-indigo-600 hover:bg-indigo-700 self-end"
                  disabled={sending || !newMessage.trim()}
                  data-testid="send-reply-btn"
                >
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </div>
            </form>
          )}
        </div>
      </main>
    </div>
  );
}
